'use client';

import { useQuality } from '@/components/shared/QualityProvider';
import { JOURNEY_PAGE_MIN_VH } from '@/lib/journeyPacing';
import { IntroScene } from './IntroScene';
import { JourneyLoader } from './JourneyLoader';

// Inline style on the journey-page section reserves the layout space
// the ScrollTrigger pin-spacer will eventually occupy. Derived from
// `projects.length` so adding a project automatically grows the
// reservation — the previous CSS-only rule was hardcoded to 1600vh and
// went out of sync the moment an 8th project was added.
const JOURNEY_PAGE_STYLE = { minHeight: `${JOURNEY_PAGE_MIN_VH}vh` } as const;

/**
 * Wraps the desktop-only journey scaffold:
 *   • section.journey-page (1600vh pin-spacer on desktop, 1200vh on mobile)
 *   • IntroScene title + scroll cue
 *   • JourneyLoader (lazy-imports the 3D corridor)
 *
 * Returns null on low tier and 'pending'. Mobile renders MobileGridIntro
 * instead so the user lands on the project register after the intro
 * logo break finishes its autoplay.
 *
 * IntroScene + JourneyLoader are kept as STATIC imports. They're client
 * components, so Next.js code-splits them into their own RSC client
 * bundle that only ships when this scaffold actually mounts (= mid/high
 * tier). Making them `next/dynamic` here adds a second async hop in dev
 * which timed out e2e canvas waits without measurable mobile gain — the
 * mobile saving comes entirely from gating TrailingBlob (see that file).
 *
 * IntroLogoBreak intentionally lives OUTSIDE this scaffold — it's a
 * fixed overlay that plays on every tier (autoplay on low). Keeping it
 * outside means the explosion still plays on mobile without dragging
 * in the rest of the journey scaffold.
 */
export function DesktopJourneyScaffold({ initialSlug }: { initialSlug?: string }) {
  const { tier } = useQuality();

  // 'low' = the mobile path below takes over (MobileGridIntro + ProjectFeed).
  // Release the vertical space so that stack sits immediately under the
  // intro logo break.
  if (tier === 'low') return null;

  // 'pending' = SSR + the first client tick before QualityProvider resolves
  // a real tier. Render an empty shell with the .journey-page class +
  // dynamic min-height so the reservation kicks in from the very first
  // paint. Without this, the InfoSection below paints at y=0 during SSR
  // and snaps down once the tier resolves to 'high' / 'mid' — measured
  // CLS of 1.0+ on desktop home.
  if (tier === 'pending') {
    return (
      <section
        className="journey-page relative"
        style={JOURNEY_PAGE_STYLE}
        aria-hidden
      />
    );
  }

  return (
    <section className="journey-page relative" style={JOURNEY_PAGE_STYLE}>
      <IntroScene />
      <JourneyLoader initialSlug={initialSlug} />
    </section>
  );
}
